import { CoreEnv } from '@eventbrite/context-gen';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { useFeatureFlags } from '@eventbrite/feature-flag-utils';
import { GlobalNav } from '@eventbrite/global-nav';
import { MlpHeader } from '@eventbrite/mlp-header';
import { getIsUserInSubscriptionSupportedTld } from '@eventbrite/pricing-and-packaging-ui';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { SiteStructure } from '@eventbrite/site-structure';
import { OrganizerSubNav } from '@eventbrite/wagtail-components';

import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
    GA_ACTION_PROFILE_DROPDOWN,
    GA_CATEGORY_HEADER,
    GA_LABEL_CREATOR_EVENT,
} from '../constants/analytics';
import './page.scss';
import { getIndependentScrolling } from './utils';

const MLP_HEADER_OVERRIDE = ['/organizer/pricing/'];

const isTldAllowedForNewPricingPage = getIsUserInSubscriptionSupportedTld();

type LinksData = {
    pk?: number;
    link?: string;
    title?: string;
};

export interface IMarketingPage extends RouteComponentProps<any, any> {
    env: CoreEnv;
    request: object;
    user: {
        canInvite?: boolean;
        canCreateEvents?: boolean;
        firstName?: string;
        fullName?: string;
        hasEvents?: boolean;
        hasOrganizers?: boolean;
        hasTikibar?: boolean;
        isAttendee?: boolean;
        isAuthenticated?: boolean;
        isStaff?: boolean;
        numOrders?: number;
        numSavedEvents?: number;
        publicId?: string;
        thumbnailUrl?: string;
        numFollowing?: number;
    };
    footerLinks: {
        popularSearches: LinksData[];
        eventsToPlan: LinksData[];
        thirdFooterColumn: LinksData[];
        countries: LinksData[];
        showMinimalLinks: boolean;
        thirdFooterColumnName: string;
    };
    children?: React.ReactChildren;
    featureFlags?: any;
}

const MarketingPage = (props: IMarketingPage) => {
    const { env, request, user, footerLinks, children, location } = props;
    const { enableIaGlobalNav } = useFeatureFlags();
    isTldAllowedForNewPricingPage &&
        MLP_HEADER_OVERRIDE.push('/organizer/compare-packages/');
    const shouldOverrideHeader = MLP_HEADER_OVERRIDE.includes(
        location?.pathname,
    );
    const hasIndependentScrolling = getIndependentScrolling(location);

    const onTrackingUserDropdown = () => {
        trackAnalyticsEvent({
            action: GA_ACTION_PROFILE_DROPDOWN,
            category: GA_CATEGORY_HEADER,
            label: GA_LABEL_CREATOR_EVENT,
        });
    };

    const url =
        env?.ebDomain && location?.pathname
            ? `https://www.${env?.ebDomain}${location?.pathname}`
            : '';

    const getMainHeader = () => {
        if (!user?.isAuthenticated && enableIaGlobalNav) {
            return <GlobalNav user={user} env={env} url={url} />;
        }
        if (shouldOverrideHeader) {
            return <MlpHeader user={user} env={env} />;
        }
    };

    const getAdditionalHeader = () => {
        if (!user?.isAuthenticated && enableIaGlobalNav) {
            return <OrganizerSubNav sticky={true} env={env} />;
        }
    };

    return (
        <SiteStructure
            env={env}
            request={request}
            user={user}
            footerLinks={footerLinks}
            showMarketingLinks={true}
            fullScreenMain={true}
            hasIndependentScrolling={hasIndependentScrolling}
            onMouseEnterGlobalHeader={onTrackingUserDropdown}
            globalHeader={getMainHeader()}
            additionalHeaderContent={
                HAS_WINDOW && !isTldAllowedForNewPricingPage
                    ? getAdditionalHeader()
                    : null
            }
        >
            {children}
        </SiteStructure>
    );
};

export default withRouter(MarketingPage);
